import React, {
    useContext,
    useState,
    useEffect
} from 'react';
import { useNavigate, useParams } from "react-router";
import { QueryClient, useQuery, useMutation } from '@tanstack/react-query';

import { getBasketData, refetchBasketData, NotificationContext, handleError } from '../router';

import * as CONSTANTS from "../constants";
import * as QUERIES from "../query-functions";
import * as UTILS from "../utils";
import * as OPTIONS from "../option-fields";

import InstanceEventSummary from '../components/instance-event-summary';
import FilterSeatsByPrice from "./filter-seats-by-price";
import FilterSeatsByArea from "./filter-seats-by-area";
import SeatingPlan from "./seating-plan";
import LoadingIndicator from "../components/loading-indicator";
import CrossIcon from '../svg/cross';

export default function SelectSeats() {

    const queryClient = new QueryClient();
    const {notification, setNotification} = useContext(NotificationContext);
    const { instanceId } = useParams();
    let navigate = useNavigate();

    const { data: basketData, isPending: basketDataPending, isFetching: basketDataFetching } = getBasketData();

    const [selectedBands, setSelectedBands] = useState(null);
    const [selectedArea, setSelectedArea] = useState(null);
    const [seatTicketIsPending, setSeatTicketIsPending] = useState(null);
    const [bandsByPrice, setBandsByPrice] = useState(null);
    const [enabledFilters, setEnabledFilters] = useState([]);
    const [ticketGroup, setTicketGroup] = useState(null);
    const [ticketsTotal, setTicketsTotal] = useState(null);

    const { data: planData } = useQuery({
        queryKey: ['planData', CONSTANTS.PLAN_ID],
        queryFn: QUERIES.getPlanData,
        staleTime: 86400000,
        refetchOnWindowFocus: false,
    });

    const {
        isPending: bandsPending,
        data: bandsData,
    } = useQuery({
        queryKey: ["bandsData"],
        queryFn: QUERIES.getBands,
        staleTime: 86400000,
    });

    const removeTicket = useMutation({
        mutationFn: (ticketId) => QUERIES.removeTickets([ticketId]),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['seatStatusData', CONSTANTS.INSTANCE_ID] });
            refetchBasketData();
        },
    });

    useEffect(() => {
        if (!bandsData) return;

        // Get the band id's from bandData and add it to bandsByPrice to be used by addBestAvailable()
        const updatedBandsArray = UTILS.getBandsByPrice(bandsData);
        setBandsByPrice(updatedBandsArray);
    }, [bandsData]);

    useEffect(() => {
        
        if (!basketData || !basketData?.tickets) {
            setTicketGroup(null);
            setTicketsTotal(null);
            return;
        };

        // To show in the continue button
        let tickets = UTILS.getTicketsByInstance(basketData.tickets);
        if (!tickets?.length) {
            setTicketGroup(null);
            setTicketsTotal(null); 
            return;
        } 

        const grouped_tickets = UTILS.groupTicketsByInstance(tickets);
        const tickets_total = UTILS.getTicketsTotal(tickets);

        setTicketGroup(grouped_tickets[0]);
        setTicketsTotal(tickets_total.toFixed(2));

    }, [basketData]);

    return (
        <div className="booking-pathway__container booking-pathway__select-seat">

        <InstanceEventSummary showAlternativeDates="true" />

        <header className='booking-pathway__select-seat--header'>
            <h1 className='booking-pathway__header'>Select seats</h1>

            <div className='booking-pathway__filters'>

                {bandsPending ?
                    <LoadingIndicator />
                :enabledFilters?.length ?
                    <>
                        {bandsByPrice?.length ?
                            <section className="booking-pathway__filters--prices">
                                    <h2 className="booking-pathway__header">Filter by price</h2>
                                    <ul className="booking-pathway__filter-list booking-pathway__filter-list--price" role="list">
                                        { bandsByPrice.map((price) =>
                                            enabledFilters.includes(price.amount) &&
                                            <li key={price.amount}>
                                                <FilterSeatsByPrice
                                                    price={price}
                                                    selectedBands={selectedBands}
                                                    setSelectedBands={setSelectedBands}
                                                    enabledFilters={enabledFilters} />
                                            </li>
                                        )}
                                    </ul>
                            </section>
                        :null
                        }
                        {planData?.areas?.length && CONSTANTS.HAS_LOCATION_FILTERS ?
                            <section className="booking-pathway__filters--areas">
                                <h2 className="booking-pathway__header">Filter by location</h2>
                                <ul className="booking-pathway__filter-list booking-pathway__filter-list--area" role="list">
                                    { planData.areas.map((area) => {
                                        if (area.type == 'Reserved' && enabledFilters.includes(area.id)) return (
                                        <li key={area.id}>
                                            <FilterSeatsByArea
                                                area={area}
                                                selectedArea={selectedArea}
                                                setSelectedArea={setSelectedArea}
                                                enabledFilters={enabledFilters} />
                                        </li>
                                    )})}
                                </ul>
                            </section>
                        :null
                        }
                    </>
                :null
                }
            </div>
        </header>

        <SeatingPlan
            planData={planData}
            selectedBands={selectedBands}
            selectedArea={selectedArea}
            setSeatTicketIsPending={setSeatTicketIsPending}
            setEnabledFilters={setEnabledFilters}
            enabledFilters={enabledFilters} />

        {ticketGroup ?
            <div className='basket-summary'>
                <h2 className="basket-summary__item--header">Your seat selection</h2>
                <ul role="list" className="basket-summary__items basket-summary__items--seats">
                    {ticketGroup.seats?.map((seat) => (
                        <li key={seat.ticket_id} className='basket-summary__item'>
                            {seat.plan_name && (
                                <span className='basket-summary__item--plan-name'>
                                    {seat.plan_name}
                                </span>
                            )}
                            {seat.seat_name && (
                                <span className='basket-summary__item--seat-name'>
                                    {seat.seat_name}
                                </span>
                            )}
                            <button type="button" onClick={() => removeTicket.mutateAsync(seat.ticket_id)} className='btn btn--remove'>
                                <span className='sr-text'>Remove ticket</span>
                                <CrossIcon />
                            </button>
                        </li>
                    ))}
                </ul>
                {OPTIONS.concessionMessage.text ?
                    <p className='booking-pathway__info-text'>{OPTIONS.concessionMessage.text.innerText}</p>
                :null}
            </div>
        :null}

        <div className="booking-pathway__footer">

            <button className='btn btn--primary'
                    type="button"
                    data-scroll-top="true"
                    onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top
                        navigate(`/book-online/${instanceId}/edit-tickets`);
                    }}
                    disabled={basketDataPending || basketDataFetching || seatTicketIsPending || !ticketsTotal}>
                        Continue
                        {basketDataPending || basketDataFetching || seatTicketIsPending ?
                            <LoadingIndicator />
                        : ticketsTotal &&
                        ` - £${ticketsTotal}`
                        }
            </button>

        </div>
    </div>
    );
}
